import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter, firstValueFrom } from 'rxjs';

import {
  AuthProviderPolicy,
  DEFAULT_PROVIDER,
  getDefaultPolicy,
  getProviderConfig,
  isProviderKey,
  ProviderKey,
  resolveProviderFromAuthRoute,
  supportsPolicy,
} from './auth.config';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);
  public readonly oauth = inject(OAuthService);

  private currentProvider: ProviderKey | null = null;
  private isConfigured = false;
  private readonly providerPolicies: Record<ProviderKey, AuthProviderPolicy> = {
    b2c: 'signin',
    entra: 'signin',
  };
  private refreshHooksConfigured = false;
  private forceReloginInProgress = false;

  private static readonly LAST_PROVIDER_KEY = 'last_provider';

  async init(provider?: ProviderKey): Promise<void> {
    this.oauth.setStorage(localStorage);
    this.ensureRefreshHooks();

    const resolvedProvider = this.resolveInitProvider(provider);
    const onCallback = this.isAuthCallbackRoute();

    await this.setProvider(resolvedProvider);

    if (this.handleAuthErrorFromUrl()) {
      return;
    }

    if (onCallback) {
      await this.handleCallback();
      return;
    }

    await this.oauth.loadDiscoveryDocument();
    await this.oauth.tryLoginCodeFlow();
    await this.switchBackToDefaultPolicyIfNeeded();
  }

  async setProvider(provider: ProviderKey): Promise<void> {
    this.currentProvider = provider;
    this.configureCurrentProvider();
    this.setupAutomaticRefresh();
    this.isConfigured = true;
    this.rememberProvider(provider);
  }

  async login(targetUrl?: string, provider?: ProviderKey): Promise<void> {
    if (provider) {
      await this.setProvider(provider);
    }

    const url = targetUrl || this.router.url || '/';
    sessionStorage.setItem('post_login_redirect', url);

    if (this.currentProvider) {
      this.rememberProvider(this.currentProvider);
    }

    await this.oauth.loadDiscoveryDocument();
    this.oauth.initCodeFlow();
  }

  logout(): void {
    this.oauth.stopAutomaticRefresh();
    this.oauth.logOut();
  }

  handleUnauthorizedFromApi(): void {
    this.forceRelogin('session_expired');
  }

  isAuthenticated(): boolean {
    return this.oauth.hasValidAccessToken();
  }

  getAccessToken(): string {
    return this.oauth.getAccessToken();
  }

  getIdClaims<T = any>(): T | null {
    return this.oauth.getIdentityClaims() as T | null;
  }

  startResetPasswordFlow(): void {
    if (this.currentProvider !== 'b2c') {
      console.warn('Reset Password flow is B2C-only.');
      return;
    }

    this.setPolicy('reset');
    void this.login(this.router.url, 'b2c');
  }

  startEditProfileFlow(): void {
    if (this.currentProvider !== 'b2c') {
      console.warn('Edit Profile flow is B2C-only.');
      return;
    }

    this.setPolicy('edit');
    void this.login(this.router.url, 'b2c');
  }

  async waitForFirstToken(): Promise<void> {
    if (this.isAuthenticated()) return;
    await firstValueFrom(
      this.oauth.events.pipe(filter((e) => e.type === 'token_received')),
    );
  }

  async handleCallback(): Promise<boolean> {
    await this.oauth.loadDiscoveryDocument();
    await this.oauth.tryLoginCodeFlow();

    if (this.handleAuthErrorFromUrl()) {
      return false;
    }

    const hasToken = this.oauth.hasValidAccessToken();

    if (hasToken && this.isAuthCallbackRoute()) {
      const storedUrl = sessionStorage.getItem('post_login_redirect') || '/';
      sessionStorage.removeItem('post_login_redirect');
      await this.router.navigateByUrl(storedUrl);
    }

    await this.switchBackToDefaultPolicyIfNeeded();
    return hasToken;
  }

  getLastProvider(): ProviderKey {
    const p = localStorage.getItem(AuthService.LAST_PROVIDER_KEY);
    if (isProviderKey(p)) {
      return p;
    }
    return DEFAULT_PROVIDER;
  }

  private resolveInitProvider(provider?: ProviderKey): ProviderKey {
    const callbackProvider = resolveProviderFromAuthRoute(this.router.url);
    if (callbackProvider) {
      return callbackProvider;
    }

    if (provider && isProviderKey(provider)) {
      return provider;
    }

    return this.getLastProvider();
  }

  private isAuthCallbackRoute(): boolean {
    return this.router.url.startsWith('/auth/');
  }

  private handleAuthErrorFromUrl(): boolean {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    const sub = params.get('error_subcode');

    if (err) {
      try {
        (this.oauth as any).logOut?.(true);
      } catch {}

      console.log(
        `something went wrong : err=() ${err}|| 'no err'), sub_code=(${sub || 'no sub_code'}), routing to /login`,
      );

      this.router.navigate(['/login'], {
        queryParams: { cancelled: '1' },
        replaceUrl: true,
      });
      return true;
    }

    return false;
  }

  private configureCurrentProvider(): void {
    if (!this.currentProvider) {
      return;
    }

    const policy = this.providerPolicies[this.currentProvider];
    this.oauth.configure(getProviderConfig(this.currentProvider, policy));
  }

  private setupAutomaticRefresh(): void {
    this.oauth.stopAutomaticRefresh();
    this.oauth.setupAutomaticSilentRefresh({}, 'access_token', true);
  }

  private ensureRefreshHooks(): void {
    if (this.refreshHooksConfigured) {
      return;
    }

    this.refreshHooksConfigured = true;
    this.oauth.events.subscribe((event) => {
      if (
        event.type === 'token_refresh_error' ||
        event.type === 'silent_refresh_error' ||
        event.type === 'silent_refresh_timeout'
      ) {
        this.forceRelogin('session_expired');
      }

      if (
        event.type === 'token_received' ||
        event.type === 'token_refreshed' ||
        event.type === 'silently_refreshed'
      ) {
        this.forceReloginInProgress = false;
      }
    });
  }

  private forceRelogin(reason: 'session_expired'): void {
    if (this.forceReloginInProgress || this.isLoginOrCallbackRoute()) {
      return;
    }

    this.forceReloginInProgress = true;
    const returnUrl = this.router.url || '/';
    sessionStorage.setItem('post_login_redirect', returnUrl);

    try {
      (this.oauth as any).logOut?.(true);
    } catch {}

    void this.router
      .navigate(['/login'], {
        queryParams: { reason, returnUrl },
        replaceUrl: true,
      })
      .finally(() => {
        this.forceReloginInProgress = false;
      });
  }

  private isLoginOrCallbackRoute(): boolean {
    const url = this.router.url || '';
    return url.startsWith('/login') || url.startsWith('/auth/');
  }

  private setPolicy(policy: AuthProviderPolicy): void {
    if (!this.currentProvider) {
      return;
    }

    if (!supportsPolicy(this.currentProvider, policy)) {
      return;
    }

    this.providerPolicies[this.currentProvider] = policy;
    this.configureCurrentProvider();
  }

  private async switchBackToDefaultPolicyIfNeeded(): Promise<void> {
    if (!this.currentProvider) {
      return;
    }

    const defaultPolicy = getDefaultPolicy(this.currentProvider);
    if (this.providerPolicies[this.currentProvider] !== defaultPolicy) {
      this.providerPolicies[this.currentProvider] = defaultPolicy;
      this.configureCurrentProvider();
    }
  }

  private rememberProvider(provider: ProviderKey): void {
    localStorage.setItem(AuthService.LAST_PROVIDER_KEY, provider);
  }
}
